import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';
import { useQuery } from '@tanstack/react-query';
import { projectService } from '@/services/projectService';
import { formatDate, getProjectLogoUrl } from '@/utils';
import { Project } from '@/types';
import { AdminSidebar } from '@/components/Layout/AdminSidebar';

export function ProjectsPage() {
  const navigate = useNavigate();
  const { user, isRoot, isAdmin } = useAuthStore();
  const { logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const itemsPerPage = 20;

  // Admin UI: allow ROLE_ADMIN and ROLE_ROOT
  if (!isRoot() && !isAdmin()) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-500">Accès refusé. Vous devez être administrateur.</p>
        </div>
      </div>
    );
  }

  // Fetch projects with pagination and search
  const {
    data: paginatedResponse,
    isLoading: projectsLoading,
    error: projectsError,
  } = useQuery({
    queryKey: ['projects', currentPage, searchQuery],
    queryFn: () => projectService.getProjects({ page: currentPage, itemsPerPage, search: searchQuery }),
    retry: 1,
  });

  const projects = paginatedResponse?.data || [];
  const totalItems = paginatedResponse?.totalItems || 0;
  const totalPages = paginatedResponse?.totalPages || 0;
  const hasNextPage = paginatedResponse?.hasNextPage || false;
  const hasPreviousPage = paginatedResponse?.hasPreviousPage || false;

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-[#0B1116] text-slate-800 dark:text-slate-200 transition-colors duration-200">
      {/* Sidebar */}
      <AdminSidebar 
        getUserInitials={getUserInitials} 
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50 dark:bg-[#0B1116]">
        {/* Header */}
        <header className="flex items-center justify-between h-16 px-6 border-b border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#16202A]">
          <div className="flex items-center gap-4 md:hidden">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="text-slate-500 hover:text-slate-600 dark:text-slate-400 dark:hover:text-white"
            >
              <span className="material-icons-outlined">menu</span>
            </button>
            <span className="font-bold text-lg dark:text-white">GTB Admin</span>
          </div>
          <div className="hidden md:block text-sm text-slate-500 dark:text-slate-400">
            <span className="text-slate-400 dark:text-slate-600">Admin</span> / Projets
          </div>
          <div className="flex items-center gap-4">
            <div className="relative hidden sm:block">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="material-icons-outlined text-slate-400 text-lg">search</span>
              </span>
              <input
                className="pl-10 pr-4 py-2 w-64 text-sm bg-slate-50 dark:bg-[#0B1116] border border-slate-200 dark:border-[#1E293B] rounded-full focus:outline-none focus:ring-2 focus:ring-primary dark:text-white placeholder-slate-400"
                placeholder="Rechercher..."
                type="text"
              />
            </div>
            <button
              onClick={toggleTheme}
              className="p-2 text-slate-400 hover:text-slate-500 dark:hover:text-white transition-colors"
              title={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
            >
              {isDark ? (
                <span className="material-icons-outlined">light_mode</span>
              ) : (
                <span className="material-icons-outlined">dark_mode</span>
              )}
            </button>
            <button className="relative p-2 text-slate-400 hover:text-slate-500 dark:hover:text-white transition-colors">
              <span className="material-icons-outlined">notifications</span>
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8">
          <div className="sm:flex sm:items-center sm:justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Liste des Projets</h1>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Gérez l'ensemble des bâtiments et installations techniques.
              </p>
            </div>
            <div className="mt-4 sm:mt-0 flex gap-3">
              <button className="inline-flex items-center px-4 py-2 border border-slate-200 dark:border-[#1E293B] rounded-lg shadow-sm text-sm font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-[#16202A] hover:bg-slate-50 dark:hover:bg-gray-800 focus:outline-none transition-colors">
                <span className="material-icons-outlined text-lg mr-2">filter_list</span>
                Filtres
              </button>
              <button
                onClick={() => navigate('/admin/projects/new')}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary hover:bg-sky-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
              >
                <span className="material-icons-outlined text-lg mr-2">add</span>
                Nouveau Projet
              </button>
            </div>
          </div>

          {/* Projects Table */}
          <div className="bg-white dark:bg-[#16202A] shadow-sm rounded-xl border border-slate-200 dark:border-[#1E293B] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 dark:border-[#1E293B]">
              <div className="relative w-full sm:w-72">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="material-icons-outlined text-slate-400">search</span>
                </div>
                <input
                  className="block w-full pl-10 pr-3 py-2 border border-slate-200 dark:border-[#1E293B] rounded-md leading-5 bg-slate-50 dark:bg-[#0B1116] text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm transition duration-150 ease-in-out"
                  placeholder="Rechercher un projet..."
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1); // Reset to first page when searching
                  }}
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 dark:divide-[#1E293B]">
                <thead className="bg-slate-50 dark:bg-[#0B1116]/50">
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider"
                      scope="col"
                    >
                      Nom
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider"
                      scope="col"
                    >
                      Adresse
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider"
                      scope="col"
                    >
                      Ville
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider"
                      scope="col"
                    >
                      Date de création
                    </th>
                    <th className="relative px-6 py-3" scope="col">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-[#16202A] divide-y divide-slate-200 dark:divide-[#1E293B]">
                  {projectsLoading ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-4 text-center text-slate-500 dark:text-slate-400">
                        Chargement...
                      </td>
                    </tr>
                  ) : projectsError ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-4 text-center">
                        <div className="text-red-500 dark:text-red-400">
                          <p className="font-medium">Erreur lors du chargement des projets</p>
                          <p className="text-sm mt-1">
                            {projectsError instanceof Error ? projectsError.message : 'Une erreur est survenue'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : projects.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-4 text-center text-slate-500 dark:text-slate-400">
                        Aucun projet trouvé
                      </td>
                    </tr>
                  ) : (
                    projects.map((project) => (
                      <tr
                        key={project.id}
                        className="hover:bg-slate-50 dark:hover:bg-[#0B1116]/50 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {project.logo ? (
                              <img
                                src={getProjectLogoUrl(project.logo) || ''}
                                alt={project.title}
                                className="flex-shrink-0 h-10 w-10 rounded-lg object-cover"
                                onError={(e) => {
                                  // Hide image if it fails to load, show icon instead
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            ) : null}
                            {(!project.logo || !getProjectLogoUrl(project.logo)) && (
                              <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center text-indigo-600 dark:text-indigo-300">
                                <span className="material-icons-outlined">business</span>
                              </div>
                            )}
                            <div className="ml-4">
                              <button
                                type="button"
                                onClick={() => navigate(`/admin/projects/${project.id}/edit`)}
                                className="text-sm font-medium text-slate-900 dark:text-white hover:underline text-left"
                                title="Éditer le projet"
                              >
                                {project.title}
                              </button>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
                          {project.adresse || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
                          {project.city || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
                          {project.createdAt ? formatDate(project.createdAt, 'dd/MM/yyyy') : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => navigate(`/admin/projects/${project.id}/areas`)}
                              className="text-slate-400 hover:text-blue-500 transition-colors"
                              title="Gérer les zones"
                            >
                              <span className="material-icons-outlined">map</span>
                            </button>
                            <button
                              onClick={() => navigate(`/admin/projects/${project.id}/equipments`)}
                              className="text-slate-400 hover:text-green-500 transition-colors"
                              title="Gérer les équipements"
                            >
                              <span className="material-icons-outlined">router</span>
                            </button>
                            <button
                              onClick={() => navigate(`/admin/super-import-equipments?projectId=${project.id}`)}
                              className="text-slate-400 hover:text-primary transition-colors"
                              title="Importer les équipements"
                            >
                              <span className="material-icons-outlined">cloud_upload</span>
                            </button>
                            <button
                              onClick={() => navigate(`/admin/projects/${project.id}/edit`)}
                              className="text-slate-400 hover:text-amber-500 transition-colors"
                              title="Éditer"
                            >
                              <span className="material-icons-outlined">edit</span>
                            </button>
                            <button
                              onClick={() => navigate(`/admin/projects/${project.id}/image`)}
                              className="text-slate-400 hover:text-indigo-500 transition-colors"
                              title="Image"
                            >
                              <span className="material-icons-outlined">image</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-4 flex items-center justify-between border-t border-slate-200 dark:border-[#1E293B] bg-slate-50 dark:bg-[#16202A]">
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Affichage de{' '}
                    <span className="font-medium text-slate-900 dark:text-white">
                      {totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}
                    </span>{' '}
                    à{' '}
                    <span className="font-medium text-slate-900 dark:text-white">
                      {Math.min(currentPage * itemsPerPage, totalItems)}
                    </span>{' '}
                    sur <span className="font-medium text-slate-900 dark:text-white">{totalItems}</span> résultats
                  </p>
                </div>
                <div>
                  {totalPages > 1 && (
                    <nav aria-label="Pagination" className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                        disabled={!hasPreviousPage}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#0B1116] text-sm font-medium text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Précédent</span>
                        <span className="material-icons-outlined text-sm">chevron_left</span>
                      </button>
                      {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            aria-current={currentPage === pageNum ? 'page' : undefined}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === pageNum
                                ? 'z-10 bg-primary/10 border-primary text-primary'
                                : 'bg-white dark:bg-[#0B1116] border-gray-300 dark:border-gray-600 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-gray-800'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                        disabled={!hasNextPage}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#0B1116] text-sm font-medium text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Suivant</span>
                        <span className="material-icons-outlined text-sm">chevron_right</span>
                      </button>
                    </nav>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
